import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import style from "./EditSkill.module.css";
import { baseurl } from "../Url";
import Loader from "../Loder/Loader";
import ToastPopup from "../ToastPopup/ToastPopup";

const EditSkill = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const USERID = localStorage.getItem("USERID");
  const OrgID = localStorage.getItem("ORGID");
  const [loading, setLoading] = useState(true);
  
  const skillName = location.state?.skillName || location.state?.skill || "";
  const [workExperience, setWorkExperience] = useState([]);
  const [education, setEducation] = useState([]);
  const [selectedExperience, setSelectedExperience] = useState([]);
  const [selectedEducation, setSelectedEducation] = useState([]);
  const [toastPopup, setToastPopup] = useState({ show: false, message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToastPopup({ show: true, message, type });
  };
  const closeToast = () => setToastPopup((p) => ({ ...p, show: false }));

  useEffect(() => {
    if (USERID) {
      fetchProfileData();
    } else {
      setLoading(false);
    }
  }, [USERID]);

  async function fetchProfileData() {
    try {
      const response = await fetch(`${baseurl}/candidate/profile/get`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: USERID,
        }),
      });

      const data = await response.json();
      
      if (data.status === true && data.data && data.data.length > 0) {
        const profile = data.data[0];
        
        if (profile.work_experience && Array.isArray(profile.work_experience)) {
          setWorkExperience(profile.work_experience);
        }
        
        if (profile.education && Array.isArray(profile.education)) {
          setEducation(profile.education);
        }
      }
    } catch (error) {
      console.error("Error fetching profile data:", error);
    } finally {
      setLoading(false);
    }
  }

  const handleExperienceToggle = (index) => {
    setSelectedExperience(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleEducationToggle = (index) => {
    setSelectedEducation(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleDeleteSkill = async () => {
    if (window.confirm("Are you sure you want to delete this skill?")) {
      try {
        if (!USERID || !OrgID) {
          showToast("User or organization ID is missing. Please log in again.", "error");
          return;
        }

        // Fetch current skills
        const response = await fetch(`${baseurl}/candidate/skills/list`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: USERID,
            org_id: OrgID,
          }),
        });

        const data = await response.json();
        
        if (data.status !== true || !data.data) {
          showToast("Failed to fetch skills: " + (data.description || "Unknown error"), "error");
          return;
        }

        const currentSkills = Array.isArray(data.data) ? data.data : [];
        const skillNameLower = (skillName || "").toLowerCase().trim();
        
        // Filter out the skill (case-insensitive, handle both string and object formats)
        const updatedSkills = currentSkills.filter(skill => {
          const skillStr = typeof skill === "string" ? skill : (skill?.name || skill?.skill || "");
          return skillStr.toLowerCase().trim() !== skillNameLower;
        });
          
        // Save updated skills
        const saveResponse = await fetch(`${baseurl}/candidate/profile/save`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: USERID,
            org_id: OrgID,
            my_skill: updatedSkills,
          }),
        });

        const saveData = await saveResponse.json();
          
        if (saveData.status === true) {
          showToast("Skill deleted successfully!");
          setTimeout(() => navigate("/profile/edit-skills"), 500);
        } else {
          showToast("Failed to delete skill: " + (saveData.description || "Unknown error"), "error");
        }
      } catch (error) {
        console.error("Error deleting skill:", error);
        showToast("An error occurred while deleting the skill", "error");
      }
    }
  };

  const handleSave = async () => {
    // Save logic can be added here if needed
    // For now, just navigate back
    navigate("/profile/edit-skills");
  };

  if (loading) {
    return <Loader />;
  }

  if (!skillName) {
    return (
      <div className={style.container}>
        <div className={style.errorMessage}>
          <p>No skill selected</p>
          <button onClick={() => navigate("/profile/edit-skills")}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className={style.container}>
      <div className={style.pageContent}>
        <div className={style.contentWrapper}>
          <h1 className={style.mainTitle}>Edit {skillName}</h1>
          
          <div className={style.instructions}>
            <p className={style.instructionText}>Tell us where you put this skill to use</p>
            <p className={style.instructionSubtext}>Select any item where this skill applies</p>
          </div>

          {/* Experience Section */}
          {workExperience.length > 0 && (
            <div className={style.section}>
              <h2 className={style.sectionTitle}>Experience</h2>
              <div className={style.checkboxList}>
                {workExperience.map((exp, index) => (
                  <label key={index} className={style.checkboxItem}>
                    <input
                      type="checkbox"
                      checked={selectedExperience.includes(index)}
                      onChange={() => handleExperienceToggle(index)}
                      className={style.checkbox}
                    />
                    <span className={style.checkboxLabel}>
                      {exp.role} at {exp.company}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Education Section */}
          {education.length > 0 && (
            <div className={style.section}>
              <h2 className={style.sectionTitle}>Education</h2>
              <div className={style.checkboxList}>
                {education.map((edu, index) => (
                  <label key={index} className={style.checkboxItem}>
                    <input
                      type="checkbox"
                      checked={selectedEducation.includes(index)}
                      onChange={() => handleEducationToggle(index)}
                      className={style.checkbox}
                    />
                    <span className={style.checkboxLabel}>
                      {edu.school || edu.degree || `Education ${index + 1}`}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Delete Skill */}
        <div className={style.deleteSection}>
          <button className={style.deleteButton} onClick={handleDeleteSkill}>
            Delete skill
          </button>
        </div>

        {/* Save Button */}
        <div className={style.saveButtonContainer}>
          <button className={style.saveButton} onClick={handleSave}>
            Save
          </button>
        </div>
      </div>

      <ToastPopup
        show={toastPopup.show}
        message={toastPopup.message}
        type={toastPopup.type}
        onClose={closeToast}
      />
    </div>
  );
};

export default EditSkill;

